import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Building, MapPin, Search, Loader2, Trash2, Plus, ArrowLeft, Eye, ExternalLink, Sparkles, Pencil, Home, CheckSquare, Square } from 'lucide-react';

interface AreaData {
  id: string;
  name: string;
  slug: string;
  is_published: boolean;
  created_at: string;
  town_id: string;
  town_name?: string;
  town_slug?: string;
  _count?: {
    listings: number;
  };
}

interface TownData {
  id: string;
  name: string;
  slug: string;
}

export default function AreaManage() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [areas, setAreas] = useState<AreaData[]>([]);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTown, setFilterTown] = useState<string>('all');
  const [deleteTarget, setDeleteTarget] = useState<AreaData | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchAreas();
    }
  }, [selectedSiteId]);

  const fetchAreas = async () => {
    if (!selectedSiteId) return;
    
    setLoading(true);
    try {
      // Get towns for this site
      const { data: townData } = await supabase
        .from('towns')
        .select('id, name, slug')
        .eq('site_id', selectedSiteId)
        .order('name');

      if (townData) {
        setTowns(townData);
      }

      // Get areas with town info
      const { data: areaData } = await supabase
        .from('areas')
        .select('id, name, slug, is_published, created_at, town_id')
        .eq('site_id', selectedSiteId)
        .order('name');

      if (areaData && townData) {
        // Add town names and get listing counts
        const areasWithInfo = await Promise.all(
          areaData.map(async (area) => {
            const town = townData.find(t => t.id === area.town_id);
            const listingsRes = await supabase
              .from('listings')
              .select('id', { count: 'exact', head: true })
              .eq('area_id', area.id);
            
            return {
              ...area,
              town_name: town?.name,
              town_slug: town?.slug,
              _count: {
                listings: listingsRes.count || 0,
              },
            };
          })
        );
        setAreas(areasWithInfo);
      }
    } catch (error) {
      console.error('Error fetching areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const { error } = await supabase.from('areas').delete().eq('id', deleteTarget.id);

      if (error) throw error;

      setAreas(areas.filter(a => a.id !== deleteTarget.id));
      toast({
        title: 'Area deleted',
        description: `${deleteTarget.name} has been removed.`,
      });
    } catch (error: any) {
      console.error('Error deleting area:', error);
      toast({
        title: 'Error deleting area',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const regenerateContent = async (area: AreaData) => {
    setRegenerating(area.id);
    try {
      const { data, error } = await supabase.functions.invoke('generate-area-content', {
        body: { areaIds: [area.id], generateImages: true },
      });

      if (error) throw error;

      toast({
        title: 'Content regeneration started!',
        description: `AI content and images for ${area.name} is being generated.`,
      });
    } catch (error: any) {
      console.error('Error regenerating content:', error);
      toast({
        title: 'Error regenerating content',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setRegenerating(null);
    }
  };

  const toggleSelectArea = (areaId: string) => {
    setSelectedAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(areaId)) {
        newSet.delete(areaId);
      } else {
        newSet.add(areaId);
      }
      return newSet;
    });
  };

  const selectAllFiltered = () => {
    const allFilteredIds = filteredAreas.map(a => a.id);
    setSelectedAreas(new Set(allFilteredIds));
  };

  const deselectAll = () => {
    setSelectedAreas(new Set());
  };

  const bulkUpdatePublishStatus = async (publish: boolean) => {
    if (selectedAreas.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const { error } = await supabase
        .from('areas')
        .update({ is_published: publish })
        .in('id', Array.from(selectedAreas));

      if (error) throw error;

      // Update local state
      setAreas(areas.map(area => 
        selectedAreas.has(area.id) 
          ? { ...area, is_published: publish }
          : area
      ));

      // Auto-trigger IndexNow when areas are published
      if (publish && selectedSiteId) {
        // Trigger for each published area
        const publishedAreaIds = Array.from(selectedAreas);
        for (const areaId of publishedAreaIds) {
          supabase.functions.invoke('auto-indexnow', {
            body: { siteId: selectedSiteId, trigger: 'area_published', entityId: areaId }
          }).catch(error => {
            console.error('Auto-IndexNow error:', error);
          });
        }
        console.log(`Auto-IndexNow triggered for ${publishedAreaIds.length} areas`);
      }

      toast({
        title: publish ? 'Areas published' : 'Areas unpublished',
        description: `${selectedAreas.size} area${selectedAreas.size !== 1 ? 's' : ''} ${publish ? 'published' : 'unpublished'}.`,
        duration: 5000,
      });

      setSelectedAreas(new Set());
    } catch (error: any) {
      console.error('Error updating areas:', error);
      toast({
        title: 'Error updating areas',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBulkUpdating(false);
    }
  };

  const filteredAreas = areas
    .filter(area => {
      const matchesSearch = 
        area.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (area.town_name && area.town_name.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesTown = filterTown === 'all' || area.town_id === filterTown;
      return matchesSearch && matchesTown;
    })
    .sort((a, b) => {
      // Sort by town name first, then by area name
      const townCompare = (a.town_name || '').localeCompare(b.town_name || '');
      if (townCompare !== 0) return townCompare;
      return a.name.localeCompare(b.name);
    });

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Building className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">Manage Areas</span>
            </div>
            <Button variant="gold" onClick={() => navigate('/admin/areas/setup')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Areas
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-display font-bold">Your Areas</h1>
          <p className="text-muted-foreground mt-1">
            {areas.length} area{areas.length !== 1 ? 's' : ''} configured
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search areas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={filterTown}
            onChange={(e) => setFilterTown(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-sm"
          >
            <option value="all">All Towns</option>
            {towns.map(town => (
              <option key={town.id} value={town.id}>
                {town.name}
              </option>
            ))}
          </select>
        </div>

        {/* Bulk Selection Bar */}
        {filteredAreas.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 p-3 bg-background border border-border rounded-lg">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={selectedAreas.size === filteredAreas.length ? deselectAll : selectAllFiltered}
              >
                {selectedAreas.size === filteredAreas.length ? (
                  <>
                    <Square className="h-4 w-4 mr-2" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Select All
                  </>
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedAreas.size} of {filteredAreas.length} selected
              </span>
            </div>
            {selectedAreas.size > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkUpdatePublishStatus(true)}
                  disabled={bulkUpdating}
                  className="text-estate-success border-estate-success hover:bg-estate-success/10"
                >
                  {bulkUpdating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-2" />
                  )}
                  Publish Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkUpdatePublishStatus(false)}
                  disabled={bulkUpdating}
                  className="text-amber-600 border-amber-600 hover:bg-amber-600/10"
                >
                  {bulkUpdating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-2" />
                  )}
                  Unpublish Selected
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Areas List */}
        {filteredAreas.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No areas yet</h3>
              <p className="text-muted-foreground mb-4">
                Add areas to start building neighborhood pages.
              </p>
              <Button variant="gold" onClick={() => navigate('/admin/areas/setup')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Areas
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAreas.map((area) => (
              <Card 
                key={area.id} 
                className={`hover:shadow-md transition-shadow ${selectedAreas.has(area.id) ? 'ring-2 ring-primary' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={selectedAreas.has(area.id)}
                        onCheckedChange={() => toggleSelectArea(area.id)}
                        className="h-5 w-5"
                      />
                      <div className={`w-3 h-3 rounded-full ${area.is_published ? 'bg-estate-success' : 'bg-amber-500'}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{area.name}</h3>
                          {area.town_name && (
                            <span className="text-sm text-muted-foreground">{area.town_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Home className="h-3 w-3" />
                            {area._count?.listings || 0} listings
                          </span>
                          <span className={area.is_published ? 'text-estate-success' : 'text-amber-600'}>
                            {area.is_published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/areas/${area.id}/content`)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => regenerateContent(area)}
                        disabled={regenerating === area.id}
                      >
                        {regenerating === area.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4 mr-1" />
                        )}
                        {regenerating === area.id ? 'Generating...' : 'Regenerate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/${area.town_slug}/${area.slug}?preview=true`, '_blank')}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      {area.is_published && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/${area.town_slug}/${area.slug}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteTarget(area)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {areas.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate('/admin/areas/setup')}>
                <Plus className="h-4 w-4 mr-2" />
                Add More Areas
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/towns')}>
                <MapPin className="h-4 w-4 mr-2" />
                Manage Towns
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this area. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
